/**
 * @typedef {Object} MetaOption
 * @property {string} [icon]
 * @property {string} value
 */

/**
 * @typedef {Object.<string, MetaOption[]>} MetaData
 */

/**
 * @typedef {Object} VariantAttribute
 * @property {string} [icon]
 * @property {string} value
 * @property {string|number} attrId
 * @property {string} attrName
 * @property {string|number} valueId
 * @property {string[]} images
 */

/**
 * @typedef {Object} RawVariant
 * @property {string|number} id
 * @property {Object.<string, VariantAttribute>} type
 * @property {number} price
 * @property {string} [title]
 * @property {string[]} images
 * @property {number} discount
 * @property {number} quantity
 * @property {boolean} is_default
 */

/**
 * @typedef {Object} TransformedVariant
 * @property {string|number} id
 * @property {Object.<string, VariantAttribute>} type
 * @property {number} price
 * @property {string} title
 * @property {string[]} images
 * @property {number} discount
 * @property {number} quantity
 * @property {boolean} is_default
 */

/**
 * @typedef {Object} TransformResult
 * @property {MetaData} meta
 * @property {TransformedVariant[]} variants
 */

/**
 * Coerce value to positive finite number, fallback to 0
 * @param {unknown} value
 * @returns {number}
 */
function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

/**
 * Check if data is a plain object (not array, null, etc.)
 * @param {unknown} data
 * @returns {boolean}
 */
function isPlainObject(data) {
  return data !== null && typeof data === 'object' && !Array.isArray(data);
}

/**
 * Validate if data has MetaData shape
 * @param {unknown} data
 * @returns {boolean}
 */
function validateMetaData(data) {
  if (!isPlainObject(data)) return false;
  return Object.values(data).every(val => Array.isArray(val));
}

/**
 * Validate if variants is an array
 * @param {unknown} variants
 * @returns {boolean}
 */
function validateVariants(variants) {
  return Array.isArray(variants);
}

/**
 * Normalize a single MetaOption entry
 * @param {unknown} option
 * @returns {MetaOption}
 */
function normalizeMetaOption(option) {
  if (!isPlainObject(option)) {
    return { value: '', icon: '' };
  }
  return {
    icon: typeof option.icon === 'string' ? option.icon : '',
    value: String(option.value ?? ''),
  };
}

/**
 * Normalize a single VariantAttribute entry
 * @param {unknown} attr
 * @returns {VariantAttribute}
 */
function normalizeVariantAttribute(attr) {
  if (!isPlainObject(attr)) {
    return {
      icon: '',
      value: '',
      attrId: '',
      attrName: '',
      valueId: '',
      images: [],
    };
  }
  return {
    icon: typeof attr.icon === 'string' ? attr.icon : '',
    value: String(attr.value ?? ''),
    attrId: String(attr.attrId ?? ''),
    attrName: String(attr.attrName ?? ''),
    valueId: String(attr.valueId ?? ''),
    images: Array.isArray(attr.images) ? [...attr.images] : [],
  };
}

/**
 * Build title from variant type attributes, sorted by attrId (numeric-aware).
 * Joins values with " | " separator. Used both during transform and in component
 * variant selection to ensure title-matching is deterministic and consistent.
 * When multiple attrs share same attrId (malformed), falls back to Object.entries insertion order.
 * @param {Object.<string, VariantAttribute>} type
 * @returns {string}
 */
export function buildVariantTitle(type) {
  if (!isPlainObject(type)) return '';

  const entries = Object.values(type)
    .map(attr => ({ attr, sortKey: String(attr?.attrId ?? '') }))
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));

  return entries
    .map(({ attr }) => attr?.value || '')
    .filter(Boolean)
    .join(' | ');
}

/**
 * Normalize a single RawVariant entry
 * @param {unknown} rawVariant
 * @param {number} index - fallback id if variant.id is missing
 * @returns {TransformedVariant|null} null if completely malformed
 */
function normalizeVariant(rawVariant, index) {
  if (!isPlainObject(rawVariant)) {
    return null;
  }

  const normalizedType = {};
  if (isPlainObject(rawVariant.type)) {
    Object.entries(rawVariant.type).forEach(([key, attr]) => {
      normalizedType[key] = normalizeVariantAttribute(attr);
    });
  }

  const title =
    typeof rawVariant.title === 'string' && rawVariant.title.trim()
      ? rawVariant.title
      : buildVariantTitle(normalizedType);

  return {
    id: rawVariant.id != null ? rawVariant.id : index,
    type: normalizedType,
    price: toNumber(rawVariant.price),
    title,
    images: Array.isArray(rawVariant.images) ? [...rawVariant.images] : [],
    discount: toNumber(rawVariant.discount),
    quantity: toNumber(rawVariant.quantity),
    is_default: Boolean(rawVariant.is_default),
  };
}

/**
 * Transform raw meta_data + variants from API into normalized shape
 * consumable by RenderVariants component. Pure, O(n).
 *
 * @param {MetaData|null|undefined} metaData
 * @param {RawVariant[]|null|undefined} variants
 * @returns {TransformResult} Always returns { meta: {}, variants: [] } shape on invalid input
 */
export function transformVariantData(metaData, variants) {
  let normalizedMeta = {};
  let normalizedVariants = [];

  if (validateMetaData(metaData)) {
    normalizedMeta = Object.fromEntries(
      Object.entries(metaData)
        .filter(([, value]) => Array.isArray(value))
        .map(([key, options]) => [key, options.map(normalizeMetaOption)])
    );
  }

  if (validateVariants(variants)) {
    normalizedVariants = variants
      .map((variant, index) => normalizeVariant(variant, index))
      .filter(Boolean);
  }

  return {
    meta: normalizedMeta,
    variants: normalizedVariants,
  };
}
